'use client'

import {useCallback, useEffect, useState} from 'react'
import {ChevronLeft, ChevronRight, Eye, Pencil, Plus, Search, Shield, Trash2, X} from 'lucide-react'
import {apiDelete, apiGet, apiPatch, apiPost, apiPut, getAuthUser} from '@/lib/api'
import {PageHeader} from '@/components/PageHeader'
import {ConfirmDialog} from '@/components/ConfirmDialog'
import {message} from '@/components/Toast'
import {Switch} from '@/components/Switch'

type SysUser = {
    id: number
    username: string
    email?: string | null
    role: string
    enabled: number
    isTrial?: number
    expiresAt?: number | null
    lastLoginAt?: number | null
    lastLoginIp?: string | null
    lastLoginLocation?: string | null
    lastSeenAt?: number | null
    online?: boolean
    createdAt: number
    instanceCount?: number
}

type LoginLog = {
    id: number
    ip: string
    ipLocation?: string | null
    userAgent: string
    createdAt: number
}

type UserAccount = {
    wxid: string
    alias?: string
    nickName: string
    avatar: string
    protocol: string
    proxyUrl: string
    status: 'online' | 'offline' | 'expired'
}

type UserDetail = {
    user: SysUser
    loginLogs: LoginLog[]
    accounts: {
        items: UserAccount[]
        total: number
        page: number
        totalPages: number
    }
}

type ExpiryMode = 'never' | 'date' | 'months'
type DetailTab = 'detail' | 'instances' | 'logs'

const PAGE_SIZE = 10
const LOGIN_LOG_PAGE_SIZE = 10
const ACCOUNT_PAGE_SIZE = 10

export function UserManageView() {
    const [users, setUsers] = useState<SysUser[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [search, setSearch] = useState('')
    const [searchInput, setSearchInput] = useState('')

    const [editUser, setEditUser] = useState<SysUser | null>(null)
    const [showForm, setShowForm] = useState(false)
    const [formUsername, setFormUsername] = useState('')
    const [formPassword, setFormPassword] = useState('')
    const [formRole, setFormRole] = useState('user')
    const [formExpiryMode, setFormExpiryMode] = useState<ExpiryMode>('never')
    const [formExpiresDate, setFormExpiresDate] = useState('')
    const [formValidMonths, setFormValidMonths] = useState('1')
    const [formEnabled, setFormEnabled] = useState(true)

    const [detail, setDetail] = useState<UserDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailTab, setDetailTab] = useState<DetailTab>('detail')
    const [accountPage, setAccountPage] = useState(1)
    const [loginLogs, setLoginLogs] = useState<LoginLog[]>([])
    const [loginLogLoading, setLoginLogLoading] = useState(false)
    const [loginLogPage, setLoginLogPage] = useState(1)
    const [loginLogTotalPages, setLoginLogTotalPages] = useState(1)
    const [loginLogTotal, setLoginLogTotal] = useState(0)
    const [deleteTarget, setDeleteTarget] = useState<SysUser | null>(null)
    const currentUser = getAuthUser()

    const fetchUsers = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true)
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(PAGE_SIZE),
            })
            if (search) params.set('search', search)
            const res = await apiGet<{
                success: boolean
                users: SysUser[]
                total: number
                totalPages: number
            }>(`/users?${params.toString()}`, {silent: true})
            if (res.success) {
                setUsers(res.users || [])
                setTotal(res.total || 0)
                setTotalPages(res.totalPages || 1)
            }
        } catch { /* ignore */ }
        finally { if (showLoading) setLoading(false) }
    }, [page, search])

    useEffect(() => { fetchUsers() }, [fetchUsers])
    useEffect(() => {
        const timer = window.setInterval(() => fetchUsers(false), 30000)
        return () => window.clearInterval(timer)
    }, [fetchUsers])

    function openCreate() {
        setEditUser(null)
        setFormUsername('')
        setFormPassword('')
        setFormRole('user')
        setFormExpiryMode('never')
        setFormExpiresDate('')
        setFormValidMonths('1')
        setFormEnabled(true)
        setShowForm(true)
    }

    function openEdit(user: SysUser) {
        setEditUser(user)
        setFormUsername(user.username)
        setFormPassword('')
        setFormRole(user.role)
        setFormExpiryMode(user.expiresAt ? 'date' : 'never')
        setFormExpiresDate(expiresAtToDateInput(user.expiresAt))
        setFormValidMonths('1')
        setFormEnabled(user.enabled === 1)
        setShowForm(true)
    }

    async function openDetail(user: SysUser, nextAccountPage = 1, nextTab: DetailTab = 'detail') {
        setDetailLoading(true)
        setDetailTab(nextTab)
        setAccountPage(nextAccountPage)
        try {
            const params = new URLSearchParams({
                accountPage: String(nextAccountPage),
                accountPageSize: String(ACCOUNT_PAGE_SIZE),
            })
            const res = await apiGet<{success: boolean} & UserDetail>(`/users/${user.id}/detail?${params.toString()}`, {silent: true})
            if (res.success) {
                const next = {
                    user: res.user,
                    loginLogs: res.loginLogs || [],
                    accounts: res.accounts || {items: [], total: 0, page: nextAccountPage, totalPages: 1},
                }
                setDetail(next)
                await fetchLoginLogs(next.user.id, 1)
            }
        } catch { /* ignore */ }
        finally { setDetailLoading(false) }
    }

    async function fetchLoginLogs(userId: number, nextPage: number) {
        setLoginLogLoading(true)
        try {
            const params = new URLSearchParams({
                page: String(nextPage),
                pageSize: String(LOGIN_LOG_PAGE_SIZE),
            })
            const res = await apiGet<{
                success: boolean
                logs: LoginLog[]
                total: number
                totalPages: number
            }>(`/users/${userId}/login-logs?${params.toString()}`, {silent: true})
            if (res.success) {
                setLoginLogs(res.logs || [])
                setLoginLogPage(nextPage)
                setLoginLogTotal(res.total || 0)
                setLoginLogTotalPages(res.totalPages || 1)
            }
        } catch { /* ignore */ }
        finally { setLoginLogLoading(false) }
    }

    async function refreshDetail() {
        if (!detail) return
        await openDetail(detail.user, accountPage, detailTab)
    }

    async function handleSaveCreate() {
        if (!formUsername.trim()) { message.error('用户名不能为空'); return }
        if (!editUser && !formPassword.trim()) { message.error('密码不能为空'); return }
        try {
            if (editUser) {
                const body: Record<string, unknown> = {
                    username: formUsername.trim(),
                    role: formRole,
                    ...expiryPayload(formRole, formExpiryMode, formExpiresDate, formValidMonths),
                }
                if (formPassword.trim()) body.password = formPassword.trim()
                await apiPut(`/users/${editUser.id}`, body)
                if ((editUser.enabled === 1) !== formEnabled) {
                    await apiPatch(`/users/${editUser.id}/enabled`, {enabled: formEnabled})
                }
                message.success('用户信息已保存')
            } else {
                await apiPost('/users', {
                    username: formUsername.trim(),
                    password: formPassword.trim(),
                    role: formRole,
                    ...expiryPayload(formRole, formExpiryMode, formExpiresDate, formValidMonths),
                })
            }
            setShowForm(false)
            await fetchUsers(false)
            await refreshDetail()
        } catch { /* api.ts handles errors */ }
    }

    async function handleDelete() {
        if (!deleteTarget) return
        try {
            await apiDelete(`/users/${deleteTarget.id}`)
            setDeleteTarget(null)
            if (detail?.user.id === deleteTarget.id) setDetail(null)
            fetchUsers()
        } catch { /* ignore */ }
    }

    function submitSearch(event: React.FormEvent) {
        event.preventDefault()
        setPage(1)
        setSearch(searchInput.trim())
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="用户管理"
                description="管理系统用户及其权限"
                action={
                    <button onClick={openCreate} className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5">
                        <Plus size={14}/> 新增用户
                    </button>
                }
            />

            <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <form onSubmit={submitSearch} className="relative w-full max-w-sm">
                        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
                        <input
                            className="input pl-9"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="搜索用户名或邮箱"
                        />
                    </form>
                    <div className="text-xs text-muted">共 {total} 个用户</div>
                </div>

                {loading ? (
                    <div className="text-center text-muted py-12">加载中...</div>
                ) : users.length === 0 ? (
                    <div className="text-center text-muted py-12">暂无用户</div>
                ) : (
                    <div className="overflow-hidden rounded-lg border border-hair bg-white">
                        <div className="overflow-x-auto overflow-y-hidden">
                        <table className="w-max min-w-[860px] text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-hair">
                                    <th className="min-w-[180px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">用户名</th>
                                    <th className="min-w-[110px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">角色</th>
                                    <th className="min-w-[100px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">状态</th>
                                    <th className="min-w-[90px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">在线</th>
                                    <th className="min-w-[180px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">有效期</th>
                                    <th className="min-w-[90px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">实例</th>
                                    <th className="min-w-[180px] whitespace-nowrap px-4 py-3 text-left font-medium text-muted">最近登录</th>
                                    <th className="min-w-[230px] whitespace-nowrap px-4 py-3 text-right font-medium text-muted">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} className="border-b border-hair/50 hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-ink">{u.username}</div>
                                            <div className="mt-0.5 text-xs text-muted">{u.email || '未绑定邮箱'}</div>
                                        </td>
                                        <td className="px-4 py-3">{roleBadge(u)}</td>
                                        <td className="px-4 py-3">{enabledBadge(u)}</td>
                                        <td className="px-4 py-3">{onlineBadge(u)}</td>
                                        <td className="px-4 py-3 text-xs text-soft whitespace-nowrap">{formatExpiryTime(u.expiresAt)}</td>
                                        <td className="px-4 py-3 text-xs text-muted">{u.instanceCount || 0} 个</td>
                                        <td className="px-4 py-3 text-xs text-soft whitespace-nowrap">{formatUserTime(u.lastLoginAt)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(u)}
                                                    className="btn-secondary h-8"
                                                >
                                                    <Pencil size={14}/>
                                                    编辑
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void openDetail(u)}
                                                    className="btn-secondary h-8"
                                                >
                                                    <Eye size={14}/>
                                                    详情
                                                </button>
                                                {u.username !== 'admin' && u.id !== currentUser?.id ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeleteTarget(u)}
                                                        className="btn-secondary h-8 px-2 text-red-500 hover:bg-red-50 hover:text-red-600"
                                                        title="删除账号"
                                                    >
                                                        <Trash2 size={14}/>
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <div className="flex items-center justify-between border-t border-hair bg-white px-4 py-3">
                            <div className="text-xs text-muted">第 {page} / {totalPages} 页</div>
                            <div className="flex items-center gap-2">
                                <button className="btn-secondary h-8" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                    <ChevronLeft size={14}/> 上一页
                                </button>
                                <button className="btn-secondary h-8" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                                    下一页 <ChevronRight size={14}/>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {detail || detailLoading ? (
                <UserDetailDrawer
                    detail={detail}
                    loading={detailLoading}
                    activeTab={detailTab}
                    accountPage={accountPage}
                    loginLogs={loginLogs}
                    loginLogLoading={loginLogLoading}
                    loginLogPage={loginLogPage}
                    loginLogTotal={loginLogTotal}
                    loginLogTotalPages={loginLogTotalPages}
                    onClose={() => setDetail(null)}
                    onTabChange={setDetailTab}
                    onAccountPage={(nextPage) => detail ? void openDetail(detail.user, nextPage, 'instances') : undefined}
                    onLoginLogPage={(nextPage) => detail ? void fetchLoginLogs(detail.user.id, nextPage) : undefined}
                />
            ) : null}

            {showForm && (
                <div className="fixed inset-0 z-[300] bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-ink mb-4">{editUser ? '编辑用户' : '新增用户'}</h3>
                        <div className="space-y-4">
                            <Field label="用户名">
                                <input type="text" value={formUsername} onChange={(e) => setFormUsername(e.target.value)}
                                    className="input h-10"/>
                            </Field>
                            <Field label="密码">
                                <input type="password" value={formPassword} onChange={(e) => setFormPassword(e.target.value)}
                                    className="input h-10"/>
                            </Field>
                            <div className="rounded border border-hair bg-gray-50 px-3 py-2">
                                <div className="text-xs text-muted">角色</div>
                                <div className="mt-1 text-sm text-ink">普通用户</div>
                            </div>
                            {editUser ? (
                                <div className="rounded border border-hair bg-gray-50 px-3 py-3 flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-xs text-muted">账号状态</div>
                                        <div className="mt-1 text-sm text-ink">{formEnabled ? '启用' : '禁用'}</div>
                                    </div>
                                    <Switch checked={formEnabled} onChange={() => setFormEnabled((value) => !value)}/>
                                </div>
                            ) : null}
                            {formRole === 'user' ? (
                                <ExpiryFields
                                    mode={formExpiryMode}
                                    expiresDate={formExpiresDate}
                                    validMonths={formValidMonths}
                                    onMode={setFormExpiryMode}
                                    onExpiresDate={setFormExpiresDate}
                                    onValidMonths={setFormValidMonths}
                                />
                            ) : null}
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setShowForm(false)} className="btn-secondary h-10">取消</button>
                            <button onClick={handleSaveCreate} className="btn-primary h-10">保存</button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!deleteTarget}
                title="删除用户"
                description={deleteTarget ? `确定要删除用户"${deleteTarget.username}"吗？此操作不可撤销。` : ''}
                confirmLabel="删除"
                danger
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </section>
    )
}

function UserDetailDrawer({
    detail,
    loading,
    activeTab,
    accountPage,
    loginLogs,
    loginLogLoading,
    loginLogPage,
    loginLogTotal,
    loginLogTotalPages,
    onClose,
    onTabChange,
    onAccountPage,
    onLoginLogPage,
}: {
    detail: UserDetail | null
    loading: boolean
    activeTab: DetailTab
    accountPage: number
    loginLogs: LoginLog[]
    loginLogLoading: boolean
    loginLogPage: number
    loginLogTotal: number
    loginLogTotalPages: number
    onClose: () => void
    onTabChange: (tab: DetailTab) => void
    onAccountPage: (page: number) => void
    onLoginLogPage: (page: number) => void
}) {
    const user = detail?.user
    const accounts = detail?.accounts
    return (
        <div className="fixed inset-0 z-[300] bg-transparent" onClick={onClose}>
            <aside
                className="absolute right-0 top-0 flex h-full w-[760px] max-w-[calc(100vw-24px)] flex-col border-l border-hair bg-white shadow-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex h-16 items-center justify-between border-b border-hair px-6">
                    <div>
                        <h3 className="text-base font-semibold text-ink">用户详情</h3>
                        <p className="text-xs text-muted">{user?.username || '加载中...'}</p>
                    </div>
                    <button type="button" className="icon-btn" onClick={onClose}><X size={16}/></button>
                </div>
                {loading || !detail || !user ? (
                    <div className="p-6 text-sm text-muted">加载中...</div>
                ) : (
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="mb-5 flex border-b border-hair bg-page">
                            <TabButton active={activeTab === 'detail'} onClick={() => onTabChange('detail')}>用户详情</TabButton>
                            <TabButton active={activeTab === 'instances'} onClick={() => onTabChange('instances')}>微信实例</TabButton>
                            <TabButton active={activeTab === 'logs'} onClick={() => onTabChange('logs')}>登录日志</TabButton>
                        </div>

                        {activeTab === 'detail' ? (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-3">
                                    <Info label="用户名" value={user.username}/>
                                    <Info label="邮箱" value={user.email || '-'}/>
                                    <Info label="角色" value={user.role === 'admin' ? '管理员' : '普通用户'}/>
                                    <Info label="账号类型" value={user.isTrial === 1 ? '试用账号' : '正式账号'}/>
                                    <Info label="状态" value={user.enabled === 1 ? '启用' : '禁用'}/>
                                    <Info label="在线状态" value={user.online ? '在线' : '离线'}/>
                                    <Info label="注册时间" value={formatUserTime(user.createdAt)}/>
                                    <Info label="账号有效期" value={formatExpiryTime(user.expiresAt)}/>
                                    <Info label="最近登录" value={formatUserTime(user.lastLoginAt)}/>
                                    <Info label="最近登录 IP" value={user.lastLoginIp || '-'}/>
                                    <Info label="最近登录地" value={user.lastLoginLocation || '-'}/>
                                </div>
                            </div>
                        ) : null}

                        {activeTab === 'instances' ? (
                            <UserAccountsTable
                                accounts={accounts?.items || []}
                                page={accountPage}
                                total={accounts?.total || 0}
                                totalPages={accounts?.totalPages || 1}
                                onPage={onAccountPage}
                            />
                        ) : null}

                        {activeTab === 'logs' ? (
                            <div>
                                {loginLogLoading ? (
                                    <div className="rounded border border-hair py-8 text-center text-sm text-muted">加载中...</div>
                                ) : loginLogs.length === 0 ? (
                                    <div className="rounded border border-hair py-8 text-center text-sm text-muted">暂无登录记录</div>
                                ) : (
                                    <div className="space-y-2">
                                        {loginLogs.map((log) => (
                                            <div key={log.id} className="rounded border border-hair p-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-ink">{log.ip || '-'}</div>
                                                        <div className="mt-0.5 truncate text-xs text-muted">{log.ipLocation || '-'}</div>
                                                    </div>
                                                    <div className="text-xs text-muted whitespace-nowrap">{formatUserTime(log.createdAt)}</div>
                                                </div>
                                                <div className="mt-2 w-full max-w-full truncate text-xs text-muted" title={log.userAgent || ''}>{log.userAgent || '-'}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-4 flex items-center justify-between border-t border-hair pt-3">
                                    <div className="text-xs text-muted">共 {loginLogTotal} 条，第 {loginLogPage} / {loginLogTotalPages} 页</div>
                                    <div className="flex items-center gap-2">
                                        <button className="btn-secondary h-8" disabled={loginLogPage <= 1 || loginLogLoading} onClick={() => onLoginLogPage(Math.max(1, loginLogPage - 1))}>
                                            <ChevronLeft size={14}/> 上一页
                                        </button>
                                        <button className="btn-secondary h-8" disabled={loginLogPage >= loginLogTotalPages || loginLogLoading} onClick={() => onLoginLogPage(Math.min(loginLogTotalPages, loginLogPage + 1))}>
                                            下一页 <ChevronRight size={14}/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </aside>
        </div>
    )
}

function UserAccountsTable({
    accounts,
    page,
    total,
    totalPages,
    onPage,
}: {
    accounts: UserAccount[]
    page: number
    total: number
    totalPages: number
    onPage: (page: number) => void
}) {
    if (accounts.length === 0) {
        return <div className="rounded border border-hair py-8 text-center text-sm text-muted">暂无微信实例</div>
    }
    return (
        <div className="overflow-hidden rounded border border-hair">
            <div className="overflow-x-auto">
                <table className="w-max min-w-[760px] table-auto text-left text-sm">
                    <colgroup>
                        <col className="w-[220px]"/>
                        <col className="w-[150px]"/>
                        <col className="w-[100px]"/>
                        <col className="w-[230px]"/>
                        <col className="w-[130px]"/>
                    </colgroup>
                    <thead className="border-b border-hair bg-gray-50 text-muted">
                    <tr>
                        <th className="whitespace-nowrap px-4 py-3 font-medium">账号信息</th>
                        <th className="whitespace-nowrap px-4 py-3 font-medium">微信账号</th>
                        <th className="whitespace-nowrap px-4 py-3 font-medium">协议</th>
                        <th className="whitespace-nowrap px-4 py-3 font-medium">代理地址</th>
                        <th className="whitespace-nowrap px-4 py-3 font-medium">状态</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {accounts.map((acc) => (
                        <tr key={acc.wxid} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                                <div className="flex items-center min-w-0">
                                    <img
                                        src={acc.avatar || '/favicon.svg'}
                                        alt="avatar"
                                        className={`mr-3 h-9 w-9 rounded-md border border-hair object-cover ${acc.status !== 'online' ? 'grayscale opacity-60' : ''}`}
                                    />
                                    <div className="min-w-0">
                                        <div className="truncate font-medium text-ink" title={acc.nickName || acc.wxid}>{acc.nickName || acc.wxid}</div>
                                        <div className="mt-0.5 truncate font-mono text-xs text-muted" title={acc.wxid}>{acc.wxid}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-4 py-3">
                                <span className="block truncate text-soft" title={acc.alias || '-'}>{acc.alias || '-'}</span>
                            </td>
                            <td className="px-4 py-3">
                                <span className="rounded border border-blue-100 bg-blue-50 px-2 py-1 font-mono text-xs text-blue-600">
                                    {acc.protocol || '-'}
                                </span>
                            </td>
                            <td className="px-4 py-3">
                                <span className="block truncate text-xs text-soft" title={acc.proxyUrl || '-'}>{acc.proxyUrl || '-'}</span>
                            </td>
                            <td className="px-4 py-3">{accountStatusBadge(acc)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between border-t border-hair bg-gray-50/60 px-4 py-3">
                <div className="text-xs text-muted">共 {total} 个实例，第 {page} / {totalPages} 页</div>
                <div className="flex items-center gap-2">
                    <button className="btn-secondary h-8" disabled={page <= 1} onClick={() => onPage(Math.max(1, page - 1))}>
                        <ChevronLeft size={14}/> 上一页
                    </button>
                    <button className="btn-secondary h-8" disabled={page >= totalPages} onClick={() => onPage(Math.min(totalPages, page + 1))}>
                        下一页 <ChevronRight size={14}/>
                    </button>
                </div>
            </div>
        </div>
    )
}

function TabButton({active, onClick, children}: {active: boolean; onClick: () => void; children: React.ReactNode}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex-1 h-9 text-sm font-medium transition-colors flex items-center justify-center ${
                active ? 'text-accent border-b-2 border-accent' : 'text-soft hover:text-ink'
            }`}
        >
            {children}
        </button>
    )
}

function ExpiryFields({
    mode,
    expiresDate,
    validMonths,
    onMode,
    onExpiresDate,
    onValidMonths,
}: {
    mode: ExpiryMode
    expiresDate: string
    validMonths: string
    onMode: (value: ExpiryMode) => void
    onExpiresDate: (value: string) => void
    onValidMonths: (value: string) => void
}) {
    return (
        <div className="space-y-3 rounded border border-hair bg-gray-50 p-3">
            <Field label="账号有效期">
                <select className="input h-10 bg-white" value={mode} onChange={(event) => onMode(event.target.value as ExpiryMode)}>
                    <option value="never">永久有效</option>
                    <option value="date">指定到期日期</option>
                    <option value="months">从现在起有效几个月</option>
                </select>
            </Field>
            {mode === 'date' ? (
                <Field label="到期日期">
                    <input className="input h-10 bg-white" type="date" value={expiresDate} onChange={(event) => onExpiresDate(event.target.value)}/>
                </Field>
            ) : null}
            {mode === 'months' ? (
                <Field label="有效月数">
                    <input
                        className="input h-10 bg-white"
                        type="number"
                        min={1}
                        max={120}
                        value={validMonths}
                        onChange={(event) => onValidMonths(event.target.value)}
                    />
                </Field>
            ) : null}
        </div>
    )
}

function Info({label, value}: {label: string; value: string}) {
    return (
        <div className="rounded border border-hair bg-gray-50 p-3">
            <div className="text-xs text-muted">{label}</div>
            <div className="mt-1 break-all text-sm text-ink">{value}</div>
        </div>
    )
}

function Field({label, children}: {label: string; children: React.ReactNode}) {
    return (
        <label className="block space-y-1.5">
            <span className="text-sm text-soft">{label}</span>
            {children}
        </label>
    )
}

function roleBadge(u: SysUser) {
    if (u.role === 'admin') {
        return <span className="inline-flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full"><Shield size={12}/> 管理员</span>
    }
    return <span className="text-xs text-muted">普通用户</span>
}

function enabledBadge(u: SysUser) {
    if (u.enabled === 1) {
        return <span className="inline-flex items-center gap-1 text-xs text-accent bg-green-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-accent"/> 启用</span>
    }
    return <span className="inline-flex items-center gap-1 text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-red-400"/> 禁用</span>
}

function onlineBadge(u: SysUser) {
    if (u.online) {
        return <span className="inline-flex items-center gap-1 text-xs text-accent bg-green-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-accent"/> 在线</span>
    }
    return <span className="inline-flex items-center gap-1 text-xs text-muted bg-gray-100 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-gray-400"/> 离线</span>
}

function accountStatusBadge(acc: UserAccount) {
    if (acc.status === 'online') {
        return <span className="inline-flex items-center whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-accent"><span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-accent"/> 在线</span>
    }
    if (acc.status === 'expired') {
        return <span className="inline-flex items-center whitespace-nowrap rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-600"><span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-orange-500"/> 会话过期</span>
    }
    return <span className="inline-flex items-center whitespace-nowrap rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-muted"><span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-gray-400"/> 离线</span>
}

function formatUserTime(value?: number | null) {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

function formatExpiryTime(value?: number | null) {
    if (!value) return '永久有效'
    const text = formatUserTime(value)
    if (text === '-') return '-'
    return value <= Date.now() ? `${text}（已过期）` : text
}

function expiresAtToDateInput(value?: number | null) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function expiryPayload(role: string, mode: ExpiryMode, expiresDate: string, validMonths: string): Record<string, unknown> {
    if (role !== 'user' || mode === 'never') return {expiresAt: null}
    if (mode === 'date') return {expiresDate: expiresDate || null}
    const months = Number.parseInt(validMonths, 10)
    return {validMonths: Number.isFinite(months) && months > 0 ? months : 1}
}
